import { FormFactoryService } from '../../../../services/form-factory.service';
import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import { FormConfig } from '../../../../models/form-config';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { InsurerDataConsts } from '../../../../../core/consts/insurer-data.consts';
import {
  AddressDataContext,
  DictionaryType,
  DictionaryVersion,
  InsurerDataFormStateService
} from '../../../../../core/services/impl/section-services/insurer-data/insurer-data-form-state.service';
import { OwnerModel } from '../../../../../core/models/owner-data/owner.model';
import { CustomMetricsModel } from '../../../../components/models/metrics/custom-metrics.model';
import { DictionaryMode, InsurerDataMode, InsurerDataModeConfig, RequiredStrategy } from '../insurer-data-mode-config';
import { SelectComponent } from '../../../../components/fields/ng-multiselect/select.component';
import {isNext, isMobile} from '../../../../../core/utils/utils';
import { debounceTime } from 'rxjs/operators';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { StepKey } from '../../../../../core/enums/step-key.enum';
import { NavigateManagerService } from '../../../../../core/services/navigate-manager.service';
import { ScrollHelper } from '../../../../services/scroll-helper';
import {TextFieldEditConfig} from '../../../../components/presentation/text-field-edit/text-field-edit.component';
import {SectionComponent} from '../../../../components/section/section.component';
import {InteractiveFieldClickEvent} from '../../../../components/abstract/abstract-field.component';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-address-data',
  templateUrl: './address-data.component.html',
  styleUrls: ['./address-data.component.scss']
})
export class AddressDataComponent implements OnInit, AfterViewInit {

  @Input() index: number;
  @Input() formGroup: FormGroup;
  @Input() formConfig: FormConfig;
  @Input() formFactoryService: FormFactoryService;
  @Input() isOwner: boolean;
  @Input() coOwner: OwnerModel;
  @Input() parentSection: SectionComponent;

  @Output() changeEvent = new EventEmitter();

  @ViewChild('citySelect') citySelect: SelectComponent;
  @ViewChild('streetSelect') streetSelect: SelectComponent;

  consts = InsurerDataConsts;
  isNext = isNext();
  firstName: string;
  lastName: string;
  pesel: string;
  postalCodeTextFieldEditConfig: TextFieldEditConfig;
  ownerNameTextFieldEditConfig: TextFieldEditConfig;
  ownerPeselTextFieldEditConfig: TextFieldEditConfig;

  private debounceTime: number;

  get sectionModeConfig(): InsurerDataModeConfig {
    return this.stateService.sectionModeConfig;
  }

  get isDictionaryMode(): boolean {
    return this.sectionModeConfig.mode === InsurerDataMode.DICTIONARY;
  }

  get isDictionaryModeDynamic(): boolean {
    return this.isDictionaryMode && this.sectionModeConfig.dictionaryMode === DictionaryMode.DYNAMIC;
  }

  get countyVisible(): boolean {
    return this.sectionModeConfig.countyVisible;
  }

  get addressHeaderText(): string {
    if (this.isOwner && this.stateService.separateInsurer) {
      return 'INSURER_DATA_SECTION.ADDRESS_DATA_COMPONENT.insurerDataHeading';
    }
    return this.isOwner ? 'INSURER_DATA_SECTION.ADDRESS_DATA_COMPONENT.ownerDataHeadingPrefix' : 'INSURER_DATA_SECTION.ADDRESS_DATA_COMPONENT.coownerDataHeadingPrefix';
  }
  constructor(public stateService: InsurerDataFormStateService,
              private navigateManager: NavigateManagerService,
              private ts: TranslateService,
              private scrollHelper: ScrollHelper) {
  }

  ngOnInit() {
    if (!this.isOwner) {
    }
    const controls = [
      this.consts.CITY,
      this.consts.BUILDING_NUMBER,
      this.consts.FLAT_NUMBER,
      this.consts.STREET
    ];
    if (this.countyVisible) {
      controls.push(this.consts.COUNTY);
    }
    this.handleControlsState(...controls);
    this.initReadonlyFields();

    if (this.isDictionaryMode) {
      this.initDictionariesUpdateEvent();
      // disable street if city isn't selected
      if (this.isDictionaryModeDynamic && !isNotNullOrUndefined(this.getControl(this.consts.CITY).value)) {
        this.disableStreet();
      }
    }
  }

  public ngAfterViewInit(): void {
    this.initSelectSearchEvent();
    this.initSelectCloseEvent();
  }

  customMetricsData(count): CustomMetricsModel {
    if (!this.isOwner) { // prepare custom metrics data for coOwners
      return {
        sectionKey: 'COOWNERS_DATA',
        count: count
      };
    }
    return null;
  }

  editPersonData(): void {
    this.navigateManager.moveToPersonalDataStep();
  }

  onFieldChange(event): void {
    this.changeEvent.emit(event);
  }

  sendCheckoutElementClickEvent(event: InteractiveFieldClickEvent): void {
    const { fieldLabel } = event;
    this.parentSection.sendCheckoutElementClickEvent({
      fieldLabel: fieldLabel,
      sectionName: this.ts.instant('INSURER_DATA_SECTION.ADDRESS_DATA_COMPONENT.ownerDataHeadingPrefix')
    });
  }

  get coOwnerInfo() {
    return `${this.firstName} ${this.lastName}`;
  }

  private initReadonlyFields() {
    this.firstName = this.isOwner ? this.stateService.insurerData.firstName : this.stateService.coownersData[this.index].firstName;
    this.lastName = this.isOwner ? this.stateService.insurerData.lastName : this.stateService.coownersData[this.index].lastName;
    this.pesel = this.isOwner ? this.stateService.insurerData.pesel : this.stateService.coownersData[this.index].pesel;
    const postalCode = this.isOwner ? this.stateService.insurerData.address.postalCode : this.stateService.coownersData[this.index].address.postalCode;

    this.postalCodeTextFieldEditConfig = {
      fieldLabel: 'INSURER_DATA_SECTION.FORM_FIELDS.postalCode',
      value: postalCode,
      actionLabel: 'edit'
    };

    this.ownerNameTextFieldEditConfig = {
      fieldLabel: 'INSURER_DATA_SECTION.FORM_FIELDS.firstNameLastName',
      value: `${this.firstName} ${this.lastName}`,
      actionLabel: 'edit',
      analyticsConfig: {
        skipFieldValue: true
      }
    };

    this.ownerPeselTextFieldEditConfig = {
      fieldLabel: 'INSURER_DATA_SECTION.FORM_FIELDS.pesel',
      value: this.pesel,
      actionLabel: 'edit',
      analyticsConfig: {
        skipFieldValue: true
      }
    };
  }

  private handleControlsState(...controlNames: string[]): void {
    if (controlNames.length === 0) {
      return;
    }

    controlNames.map(controlName => {
      this.formGroup.controls[controlName].valueChanges.subscribe(value => {
        if (this.isOwner) {
          this.stateService.changeAdressRegistrationData(controlName, value, { isCoOwner: false });
        } else {
          this.stateService.changeAdressRegistrationData(controlName, value, { isCoOwner: true, coOwnerIndex: this.index });
        }
        if (this.stateService.isStreetSelect && controlName === this.consts.CITY) {
          this.enableStreet();
          if (!this.isDictionaryModeDynamic && isNotNullOrUndefined(value)) {
            this.formFactoryService.fieldConfig(this.consts.STREET).select.loadingEvent.next(true);
          }
          this.formFactoryService.fieldConfig(this.consts.STREET).options['items'] = [];
          if (this.isDictionaryModeDynamic) {
            this.getSelectComponent(this.consts.STREET).dynamicDictionaryEvent.emit({
              items: [],
              noResultsText: this.stateService.getAddressItemConfig(this.consts.STREET).typeForResultsMessage
            });
          } else {
            this.getSelectComponent(this.consts.STREET).dynamicDictionaryEvent.emit({
              items: [],
              noResultsText: null
            });
          }
          this.resetField(this.consts.STREET);
        }
      });
    });
  }

  private getControl(key: string): AbstractControl {
    return this.formGroup.controls[key];
  }

  private initDictionariesUpdateEvent(): void {
    this.stateService.updateDictionaryEvent.subscribe(data => {
      if (data.dictionaryType === DictionaryType.CITIES) {
        if (!data.isCoOwner && this.isOwner) {
          this.updateItems(DictionaryType.CITIES, { isCoOwner: false, version: data.version });
        } else if (data.isCoOwner && !this.isOwner && data.coOwnerIndex === this.index) {
          this.updateItems(DictionaryType.CITIES, { isCoOwner: true, coOwnerIndex: this.index, version: data.version });
        }
        if (data.forceSelectClose) {
          this.citySelect.closeSelect();
        }
      } else {
        if (!data.isCoOwner && this.isOwner) {
          this.updateItems(DictionaryType.STREETS, { isCoOwner: false, version: data.version });
        } else if (data.isCoOwner && !this.isOwner && data.coOwnerIndex === this.index) {
          this.updateItems(DictionaryType.STREETS, { isCoOwner: true, coOwnerIndex: this.index, version: data.version });
        }
        if (data.forceSelectClose) {
          this.streetSelect.closeSelect();
        }
      }
    });
  }

  private resetField(fieldKey: string): void {
    this.getControl(fieldKey).setValue(null);
  }

  private initSelectSearchEvent(): void {
    if (this.isDictionaryModeDynamic) {
      this.debounceTime = this.sectionModeConfig.debounceTime;
      this.formFactoryService.fieldConfig(this.consts.CITY).select.searchEvent
        .pipe(debounceTime(this.debounceTime))
        .subscribe(searchTerm => this.handleSearch(this.consts.CITY, searchTerm));
      this.formFactoryService.fieldConfig(this.consts.STREET).select.searchEvent
        .pipe(debounceTime(this.debounceTime))
        .subscribe(searchTerm => this.handleSearch(this.consts.STREET, searchTerm));
    }
  }

  private initSelectCloseEvent(): void {
    if (this.isDictionaryMode && this.sectionModeConfig.dictionaryMode === DictionaryMode.DYNAMIC) {
      this.formFactoryService.fieldConfig(this.consts.CITY).select.closeEvent.subscribe(() => this.handleClose(this.consts.CITY));
      this.formFactoryService.fieldConfig(this.consts.STREET).select.closeEvent.subscribe(() => this.handleClose(this.consts.STREET));
    }
  }

  private handleSearch(fieldKey: string, searchTerm: string): void {
    if (!isMobile()) {
      this.getSelectComponent(fieldKey).open();
    }
    this.requestFilteredDictionary(fieldKey, searchTerm);
  }

  private handleClose(fieldKey: string): void {
    const type = this.getDictionaryTypeForField(fieldKey);
    const currentVersion = this.stateService.getCurrentDictionaryVersion(type, { isCoOwner: !this.isOwner, coOwnerIndex: this.index });
    if (currentVersion === DictionaryVersion.FILTERED) {
      this.stateService.resetDictionaryToInitial(type, { isCoOwner: !this.isOwner, coOwnerIndex: this.index });
    }
  }

  private requestFilteredDictionary(fieldKey: string, searchTerm: string): void {
    if (this.loadInitialDictionary(fieldKey, searchTerm)) { // load initial dictionary if searchTerm is empty
      const type = this.getDictionaryTypeForField(fieldKey);
      this.setNotFoundText(fieldKey, this.stateService.getAddressItemConfig(fieldKey).typeForResultsMessage);
      this.updateItems(type, { isCoOwner: !this.isOwner, coOwnerIndex: this.index, version: DictionaryVersion.INITIAL });
      this.getSelectComponent(fieldKey).dynamicDictionaryEvent.emit({ items: [], noResultsText: this.stateService.getAddressItemConfig((fieldKey)).typeForResultsMessage } );
      return;
    }
    this.setLoadingDictionaryState(fieldKey);
    switch (fieldKey) {
      case InsurerDataConsts.CITY:
        this.stateService.requestCities({ isCoOwner: !this.isOwner, coOwnerIndex: this.index, version: DictionaryVersion.FILTERED }, searchTerm);
        break;
      case InsurerDataConsts.STREET:
        this.stateService.requestStreets({ isCoOwner: !this.isOwner, coOwnerIndex: this.index, version: DictionaryVersion.FILTERED }, searchTerm);
        break;
    }
  }

  private loadInitialDictionary(fieldKey: string, searchTerm: string): boolean {
    const { searchTermMinLength } = this.stateService.getAddressItemConfig(fieldKey);
    if (isNotNullOrUndefined(searchTermMinLength)) {
      return searchTerm.length < searchTermMinLength;
    }
    return false; // searchTermMinLength is undefined - load filtered dictionary
  }

  private updateItems(type: DictionaryType, context: AddressDataContext): void {
    let items = this.stateService.getDictionaryForSpecificVersion(type, context);
    switch (type) {
      case DictionaryType.CITIES:
        if (!this.stateService.canShowInitialDictionary(this.consts.CITY) && context.version === DictionaryVersion.INITIAL) {
          items = [];
          this.setNotFoundText(this.consts.CITY, this.stateService.getAddressItemConfig(this.consts.CITY).typeForResultsMessage);
        } else {
          this.setNotFoundText(this.consts.CITY, null);
        }
        this.formFactoryService.fieldConfig(this.consts.CITY).options['items'] = items;
        this.formFactoryService.fieldConfig(this.consts.CITY).select.loadingEvent.next(false);
        break;
      case DictionaryType.STREETS:
        if (!this.stateService.canShowInitialDictionary(this.consts.STREET) && context.version === DictionaryVersion.INITIAL) {
          if (this.stateService.streetRequiredStrategy === RequiredStrategy.REQUIRED_IF_DICTIONARY_EXISTS) {
            this.updateStreetValidators(!!items.length);
          }
          if (!items.length) { // disable street when dict is empty and dict is result of city change
            this.disableStreet();
          } else {
            items = [];
            this.setNotFoundText(this.consts.STREET, this.stateService.getAddressItemConfig(this.consts.STREET).typeForResultsMessage);
          }
        } else {
          this.setNotFoundText(this.consts.STREET, null);
        }
        this.formFactoryService.fieldConfig(this.consts.STREET).options['items'] = items;
        this.formFactoryService.fieldConfig(this.consts.STREET).select.loadingEvent.next(false);
        break;
    }
  }

  /**
   * updates validators for street field
   */
  private updateStreetValidators(required: boolean): void {
    if (required) {
      this.getControl(this.consts.STREET).setValidators([Validators.required]);
    } else {
      this.getControl(this.consts.STREET).clearValidators();
    }
    this.getControl(this.consts.STREET).updateValueAndValidity({ emitEvent: false });
  }

  private setLoadingDictionaryState(fieldKey: string): void {
    this.formFactoryService.fieldConfig(fieldKey).options['items'] = [];
    this.formFactoryService.fieldConfig(fieldKey).select.loadingEvent.next(true);
  }

  private getSelectComponent(fieldKey: string): SelectComponent {
    switch (fieldKey) {
      case this.consts.CITY:
        return this.citySelect;
      case this.consts.STREET:
        return this.streetSelect;
    }
  }

  private getDictionaryTypeForField(fieldKey: InsurerDataConsts): DictionaryType {
    switch (fieldKey) {
      case InsurerDataConsts.CITY:
        return DictionaryType.CITIES;
      case InsurerDataConsts.STREET:
        return DictionaryType.STREETS;
    }
  }

  private disableStreet(): void {
    if (!this.getControl(this.consts.STREET).disabled) {
      this.getControl(this.consts.STREET).disable();
    }
  }

  private enableStreet(): void {
    if (this.getControl(this.consts.STREET).disabled) {
      this.getControl(this.consts.STREET).enable();
    }
  }

  private setNotFoundText(fieldKey: string, message: string): void {
    this.getSelectComponent(fieldKey).setNotFoundText(message);
  }
}
